import { useEffect } from 'react'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { useAppSelector } from 'app/hooks'
import { selectSystemScopeIdContainer } from 'app/layout/Navbar/Filters/globalFilterSelectors'
import {
  IExtendedColDef,
  useGetDataManagementDefaultDataViewColumnDefinitionsQuery
} from 'app/services/api/agGrid/agGrid'
import {
  CBVSupportedViewType,
  IColorByValueAttributeMap,
  IColorByValueConfig,
  useGetColorByValueConfigQuery
} from 'app/services/api/colorByValue'
import {
  cbvSupportedBackendTypes,
  getSimplifiedType
} from 'common/utils/cbvUtils'

import {
  generateColorRangeForBooleanType,
  generateColorRangeForDateType,
  generateColorRangeForNumberType,
  generateColorRangeForStringType
} from './cbvColorMapGenerator'
import { getHexCodesFromCBVTheme } from './cbvThemes'

interface IColorByValueConfigHook {
  config: IColorByValueConfig | undefined
  setConfig: React.Dispatch<
    React.SetStateAction<IColorByValueConfig | undefined>
  >
}

/**
 * Schema v3: cache bust to resolve duplicate options post API fix
 */
const CURRENT_CBV_SCHEMA_VERSION = 4

/**
 * Retrieve and bind IColorByValueAttributeColorConfig[] from server or
 * initialize to the default theme if empty.
 */
const useInitCBVConfigEffect = ({
  config,
  setConfig
}: IColorByValueConfigHook) => {
  // Deconstruct config for referential equal types
  const { viewType, viewId, viewName } = config ?? {}
  const systemScopeIdContainer = useAppSelector(selectSystemScopeIdContainer)

  // CBV Persist API
  const { currentData: serverSideCBVConfig, error: serverSideCBVError } =
    useGetColorByValueConfigQuery(
      {
        feature:
          viewType ?? CBVSupportedViewType.dataManagementDefaultAttributesView,
        id: viewId
      },
      { skip: viewType == null }
    )

  // #region ColDefs API for each supported view
  const { currentData: defaultDataViewColDefs } =
    useGetDataManagementDefaultDataViewColumnDefinitionsQuery(
      {
        params: {
          viewId: viewId!,
          systemScopeId: systemScopeIdContainer.id!
        },
        cacheKeys: {
          systemScopeUpdatedAt: systemScopeIdContainer.updatedAt
        }
      },
      {
        skip:
          viewType !==
            CBVSupportedViewType.dataManagementDefaultAttributesView ||
          viewId == null ||
          systemScopeIdContainer.id == null
      }
    )
  // #endregion

  /**
   * On init / Server side CBV update:
   * - attempt bind server side CBV data
   * - fallback to base template if no server side data
   * - set success/fail state
   */
  useEffect(() => {
    if (viewType == null) {
      return
    }

    // Default fallback
    const newConfig: IColorByValueConfig = {
      version: CURRENT_CBV_SCHEMA_VERSION,
      loadStatus: 'init',
      viewType,
      viewId,
      viewName: viewName ?? '',
      selectedAttributeId: null,
      attributesMap: []
    }

    // Add in server side data if available
    if (serverSideCBVConfig != null) {
      newConfig.putId = serverSideCBVConfig.putId

      if (serverSideCBVConfig.version !== CURRENT_CBV_SCHEMA_VERSION) {
        console.warn(
          [
            'Non matching CBV version detected.',
            `Found: ${serverSideCBVConfig.version}, Supported: ${CURRENT_CBV_SCHEMA_VERSION}`,
            'Resetting structure'
          ].join(' ')
        )
      } else {
        newConfig.selectedAttributeId = serverSideCBVConfig.selectedAttributeId
        newConfig.attributesMap = serverSideCBVConfig.attributesMap
      }
    }

    const attributeMetaList: IColorByValueAttributeMap[] = [
      // data array from RTKQ is immutable
      ...newConfig.attributesMap
    ]

    let currentViewColDefs: IExtendedColDef[] = []

    // #region assign active view ColDef
    if (
      viewType === CBVSupportedViewType.dataManagementDefaultAttributesView &&
      defaultDataViewColDefs != null
    ) {
      currentViewColDefs = defaultDataViewColDefs
    }
    // #endregion

    for (const cd of currentViewColDefs) {
      // NOTE: date currently explicitly not supported in CBV
      if (!cd.cbvAllowed || cd.dataType == null) {
        continue
      }

      if (!cbvSupportedBackendTypes.includes(cd.dataType)) {
        console.warn('Unsupport CBV type found:', cd.dataType)

        continue
      }

      // NOTE: data source column now has unique ID
      const attributeIdx = attributeMetaList.findIndex(
        (colorConfig) => colorConfig.attributeId === cd.id?.toString()
      )

      if (attributeIdx >= 0) {
        /**
         * If attribute has been initialized previously
         * We only want to refresh range info
         */
        const attributeMeta: IColorByValueAttributeMap = {
          ...attributeMetaList[attributeIdx],
          attributeType: getSimplifiedType(cd.dataType),
          attributeTypeRange: {}
        }

        switch (getSimplifiedType(cd.dataType)) {
          case 'boolean':
            // noop
            break
          case 'number': {
            const min = cd.minValue ?? 0
            const max = cd.maxValue ?? 10000

            attributeMeta.attributeTypeRange.number = { min, max }

            break
          }

          case 'date': {
            const minDate = cd.minDate
              ? new Date(cd.minDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0]
            const maxDate = cd.maxDate
              ? new Date(cd.maxDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0]

            attributeMeta.attributeTypeRange.date = { minDate, maxDate }

            break
          }

          case 'string': {
            const stringSet = cd.attributeEnums ?? []

            attributeMeta.attributeTypeRange.string = stringSet
          }
        }

        attributeMetaList.splice(attributeIdx, 1)
        // Maintain consistent ordering
        attributeMetaList.push(attributeMeta)
      } else {
        // First time initializing the attribute
        const attributeMeta: IColorByValueAttributeMap = {
          attributeId: cd.id?.toString() ?? 'ATTR ID MISSING',
          attributeCode: cd.code ?? cd.field ?? 'ATTR CODE MISSING',
          attributeName: cd.name ?? cd.headerName ?? 'ATTR NAME MISSING',
          attributeType: getSimplifiedType(cd.dataType),
          attributeTypeRange: {},
          iconTheme: 'lighthouse',
          configurations: []
        }

        switch (getSimplifiedType(cd.dataType)) {
          case 'boolean':
            attributeMeta.configurations = generateColorRangeForBooleanType(
              getHexCodesFromCBVTheme(attributeMeta.iconTheme)
            )

            break
          case 'number': {
            const min = cd.minValue ?? 0
            const max = cd.maxValue ?? 10000

            attributeMeta.attributeTypeRange.number = { min, max }

            const isYearValue = cd.code?.includes('Year')

            attributeMeta.configurations = generateColorRangeForNumberType(
              getHexCodesFromCBVTheme(attributeMeta.iconTheme),
              min,
              max,
              // Initialize as single option if range is the exact same
              min === max ? 1 : undefined,
              isYearValue
            )

            break
          }

          case 'date': {
            const minDate = cd.minDate
              ? new Date(cd.minDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0]
            const maxDate = cd.maxDate
              ? new Date(cd.maxDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0]

            attributeMeta.attributeTypeRange.date = { minDate, maxDate }

            attributeMeta.configurations = generateColorRangeForDateType(
              getHexCodesFromCBVTheme(attributeMeta.iconTheme),
              minDate,
              maxDate,
              minDate === maxDate ? 1 : undefined
            )

            break
          }

          case 'string': {
            const stringSet = cd.attributeEnums ?? []

            attributeMeta.attributeTypeRange.string = stringSet

            attributeMeta.configurations = generateColorRangeForStringType(
              getHexCodesFromCBVTheme(attributeMeta.iconTheme),
              stringSet
            )
          }
        }

        attributeMetaList.push(attributeMeta)
      }
    }

    newConfig.attributesMap = attributeMetaList

    // Status update
    if (serverSideCBVError != null) {
      if ((serverSideCBVError as FetchBaseQueryError)?.status === 404) {
        newConfig.loadStatus = 'done'
      } else {
        newConfig.loadStatus = 'fail'
      }
    } else if (serverSideCBVConfig != null) {
      newConfig.loadStatus = 'done'
    }

    setConfig(newConfig)
  }, [
    defaultDataViewColDefs,
    serverSideCBVConfig,
    serverSideCBVError,
    viewId,
    viewName,
    viewType,
    setConfig
  ])
}

export default useInitCBVConfigEffect
